import * as sessionReplay from "@amplitude/session-replay-browser"
import { get } from "@ember/object"
import * as Sentry from "@sentry/browser"
import { analytics } from "cc-frontend/error-handling"
import { getAmplitudeSessionId } from "cc-frontend/lib/amplitude"
import type Session from "cc-frontend/services/session"
import { assign, capitalize, isNil, map, omitBy, reduce, words } from "lodash-es"

export default function (actionStream, session: Session) {
  actionStream.onValue(([_type, _payload, actionDoc]) => {
    if (actionDoc.attributes.narrative === null) return
    let humanizedContext = reduce(
      actionDoc.attributes.narrative.context,
      (acc, v, k) => {
        return assign(acc, {
          [map(words(k), capitalize).join(" ")]: v,
        })
      },
      {}
    )
    let title = actionDoc.attributes.narrative.titleForAnalytics
    let context: Record<string, any> = assign({}, humanizedContext, {
      Description: get(actionDoc.attributes.narrative, "description"),
      "Action Id": actionDoc.id,
      "Session Id": actionDoc.relationships.session.data.id,
      // "Sequence Number":           actionDoc.relationships.originatingUser.data.id,
      "Seconds Since Last Action": actionDoc.attributes.timing.secondsSinceLastClientAction,
      URL: typeof window.location !== "undefined" ? window.location.href : null,
    })
    context = omitBy(context, isNil)

    track(session, title, context, {
      groupId: actionDoc.attributes.narrative.context?.groupId,
    })

    // We don't need to send all our events to plausible -- it's not doing anything useful
    // if (typeof plausible !== "undefined" && window.plausible && title) {
    //   window.plausible(title, context)
    // }

    Sentry.addBreadcrumb({
      message: actionDoc.attributes.name,
      category: "action",
      data: Object.assign({}, humanizedContext, {
        Title: title,
        Description: get(actionDoc.attributes.narrative, "description"),
        "Action Id": actionDoc.id,
      }),
    })
  })
}

export function track(
  session: Session,
  title: string,
  properties: Record<string, any>,
  context?: Record<string, any>
) {
  if (title) {
    // Don't send anonymous Page - Visit events to Segment.
    // They are consuming too many Segment MTUs.
    if (title === "Page - Visit" && !session.userId) return

    analytics.track(
      title,
      // https://amplitude.com/docs/session-replay/session-replay-integration-with-segment#amplitude-actions-destination
      { ...properties, ...sessionReplay.getSessionReplayProperties() },
      {
        integrations: Object.assign({
          Amplitude: {
            // Let amplitude group sessions
            // https://segment.com/docs/connections/destinations/catalog/amplitude/
            // We also set this in our next-js frontend so it transfers between
            // code bases and before a user has a session.
            session_id: getAmplitudeSessionId(),
          },
        }),
        context: context || {},
      }
    )
    // Send our foo page visits as analytics.page()
    if (title === "Page - Visit") {
      analytics.page(
        "App",
        properties["Route Name"],
        {
          fromPage: properties["From Page"],
          fromPagePlacement: properties["From Page Placement"],
        },
        {
          // This is just duplicative for amplitude.
          integrations: {
            "Amplitude (Actions)": false,
          },
        }
      )
    }
  }
}
